import React from 'react';
import { Link, Navigate } from 'react-router-dom';
import { Loader2, UserPlus, Copy, Check, Pencil, X, ChevronRight, Download } from 'lucide-react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type {
  CommissionReport,
  SalespersonLinkStatus,
  SelfServiceSalesperson,
  SelfServiceSubmissionRow,
} from '../../types/selfService';
import { suggestReferralCode } from '../../types/selfService';
import { useSingleStationContext } from '../../components/self-service/admin/useAdminContext';
import AdminPageHeader from '../../components/self-service/admin/AdminPageHeader';
import { useAuth } from '../../components/self-service/auth/useAuth';
import { rankOf } from '../../components/self-service/shared/roleRank';
import { ApiError, apiClient } from '../../lib/apiClient';

const formatDollars = (cents: number) =>
  (cents / 100).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const formatDollarAxis = (dollars: number) =>
  `$${dollars.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;

const PublisherReferralsPage: React.FC = () => {
  const { slug } = useSingleStationContext();
  const { user } = useAuth();
  // CTV-671: commission data is manager+ — the API refuses sales_rep on
  // every endpoint this page calls, so don't render (or fetch) at all.
  const allowed = rankOf(user?.rawRole) >= rankOf('manager');
  const [rows, setRows] = React.useState<SelfServiceSalesperson[]>([]);
  const [report, setReport] = React.useState<CommissionReport | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [editorOpen, setEditorOpen] = React.useState(false);
  const [editing, setEditing] = React.useState<SelfServiceSalesperson | null>(null);
  const [copiedCode, setCopiedCode] = React.useState<string | null>(null);
  const [exporting, setExporting] = React.useState(false);

  const refresh = React.useCallback(async () => {
    setLoading(true);
    try {
      // The list gives the roster; the report gives net revenue + commission
      // per rep from the payout ledger (net of rev share and Stripe fees).
      const [listData, reportData] = await Promise.all([
        apiClient.post<{ rows: SelfServiceSalesperson[]; default_commission_percent: number | null }>(
          '/salespeople/list',
          { slug },
        ),
        apiClient.post<CommissionReport>('/salespeople/commission-report', { slug }),
      ]);
      setRows(listData.rows ?? []);
      setReport(reportData);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [slug]);

  React.useEffect(() => {
    if (allowed) refresh();
  }, [refresh, allowed]);

  // CTV-673: resend state — hooks must precede the role gate's early return.
  const [resendingId, setResendingId] = React.useState<string | null>(null);
  const [resentId, setResentId] = React.useState<string | null>(null);

  if (!allowed) {
    return <Navigate to={`/admin/${slug}`} replace />;
  }

  const upsert = async (payload: {
    id?: string;
    name: string;
    email?: string | null;
    code: string;
    defaultDiscountPercent?: number | null;
    commissionPercent?: number | null;
    active?: boolean;
  }) => {
    try {
      await apiClient.post('/salespeople', { slug, ...payload });
      await refresh();
    } catch (e) {
      if (e instanceof ApiError && (e.status === 409 || e.message === 'code_in_use')) {
        throw new Error('That code is already used by another rep.');
      }
      throw e instanceof Error ? e : new Error('Save failed');
    }
  };

  const repReport = (rep: SelfServiceSalesperson) =>
    report?.reps.find((r) => r.salesperson_id === rep.id) ?? null;

  // CTV-673: re-send the sign-up invite for one rep. On success the roster
  // refreshes (the row may even flip to Linked if an account appeared).
  const handleResendInvite = async (id: string) => {
    setResendingId(id);
    try {
      await apiClient.post('/salespeople/resend-invite', { slug, id });
      setResentId(id);
      setTimeout(() => setResentId((v) => (v === id ? null : v)), 2000);
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to send invite');
    } finally {
      setResendingId(null);
    }
  };

  // Per-salesperson attribution rollup — campaigns sold, approved revenue,
  // and the ledger-backed net revenue + commission. Pulls all submissions
  // on demand so we don't keep them in memory just for the export button.
  const handleExport = async () => {
    if (rows.length === 0) return;
    setExporting(true);
    try {
      const { rows: subs } = await apiClient.post<{ rows: SelfServiceSubmissionRow[] }>(
        '/submissions/list-by-publisher',
        { slug },
      );

      const lines: string[][] = [[
        'Name', 'Email', 'Code', 'Active', 'Bonus plays %',
        'Commission %',
        'Total submissions', 'Approved', 'Pending review',
        'Approved revenue',
        'Net revenue', 'Commission',
        'First attribution', 'Last attribution',
      ]];
      for (const rep of rows) {
        const attributed = (subs ?? []).filter((s) => s.referral_code === rep.code);
        // Status enum migrated: 'captured' → 'approved', 'authorized' → 'payment_authorized'.
        const captured = attributed.filter((s) => s.status === 'approved');
        const authorized = attributed.filter((s) => s.status === 'payment_authorized');
        const capturedRevenueCents = captured.reduce((acc, s) => acc + s.amount_cents, 0);
        const sortedDates = attributed.map((s) => s.created_at).sort();
        const rr = repReport(rep);
        lines.push([
          rep.name,
          rep.email ?? '',
          rep.code,
          rep.active ? 'yes' : 'no',
          rep.default_discount_percent != null ? String(rep.default_discount_percent) : '',
          rr?.effective_commission_percent != null ? String(rr.effective_commission_percent) : '',
          String(attributed.length),
          String(captured.length),
          String(authorized.length),
          (capturedRevenueCents / 100).toFixed(2),
          rr ? (rr.total_net_revenue_cents / 100).toFixed(2) : '',
          rr ? (rr.total_commission_cents / 100).toFixed(2) : '',
          formatCsvDate(sortedDates[0]),
          formatCsvDate(sortedDates[sortedDates.length - 1]),
        ]);
      }
      downloadCsv(
        `referrals-${slug}-${new Date().toISOString().slice(0, 10)}.csv`,
        lines.map((r) => r.map(csvCell).join(',')).join('\r\n'),
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  // Build the share URL the salesperson can hand out. Uses the current
  // origin so devs see localhost links and prod sees the real host.
  const shareUrl = (code: string) => {
    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    return `${origin}/${slug}?ref=${code}`;
  };

  const handleCopy = async (code: string) => {
    try {
      await navigator.clipboard.writeText(shareUrl(code));
      setCopiedCode(code);
      setTimeout(() => setCopiedCode((c) => (c === code ? null : c)), 1500);
    } catch {
      // ignore — fall back to selecting the input field
    }
  };

  return (
    // max-w-6xl, not the admin default 5xl: the roster table is the widest in
    // the publisher admin and Advertisers already uses 6xl for the same reason.
    <div className="max-w-6xl">
        {/* CONTEXT.md flags Referrals as scope-flexible by the reading-rows
            test, but CTV-759 deliberately ships only Overview, Submissions
            and Advertisers. It is the next candidate, not a new concept. */}
        <AdminPageHeader
          title="Referrals"
          scopeMode="bound"
          currentSlug={slug}
          subtitle="Give each salesperson a code so campaigns they bring in are credited to them. Past attributions stay locked even if a code is later disabled."
          actions={
            <>
              <button
                type="button"
                onClick={handleExport}
                disabled={rows.length === 0 || exporting}
                className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 hover:bg-slate-50 rounded-md whitespace-nowrap disabled:opacity-50"
              >
                {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                Export CSV
              </button>
              <button
                type="button"
                onClick={() => { setEditing(null); setEditorOpen(true); }}
                className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md whitespace-nowrap"
              >
                <UserPlus className="w-4 h-4" />
                Add Salesperson
              </button>
            </>
          }
        />

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-sm text-red-700 flex items-start justify-between gap-3">
            <span>{error}</span>
            <button onClick={() => setError(null)} aria-label="Dismiss"><X className="w-4 h-4" /></button>
          </div>
        )}

        {loading ? (
          <div className="text-sm text-slate-500"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading…</div>
        ) : (
          <>
            {report && (
              <DefaultCommissionCard
                slug={slug}
                value={report.default_commission_percent}
                onSaved={refresh}
                onError={setError}
              />
            )}

            {report && <RepRevenueChart report={report} />}

            {rows.length === 0 ? (
              <div className="bg-white border border-slate-200 rounded-xl p-10 text-center">
                <UserPlus className="w-8 h-8 text-slate-400 mx-auto mb-2" />
                <p className="text-slate-700 font-medium">No salespeople yet</p>
                <p className="text-sm text-slate-500 mt-1">Add your first rep so campaigns they refer can be credited to them.</p>
              </div>
            ) : (
              <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
                {/* Nine columns outgrow the card on a laptop window. Without a
                    scroll container the card's overflow-hidden clips the header
                    row and the Actions column instead of letting them scroll. */}
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-[10px] font-medium uppercase tracking-[0.12em] text-slate-500 bg-slate-50/60 border-b border-slate-200">
                        <th className="px-5 py-3 font-medium">Name</th>
                        <th className="px-3 py-3 font-medium">Code</th>
                        <th className="px-3 py-3 font-medium">Bonus plays</th>
                        <th className="px-3 py-3 font-medium">Commission</th>
                        <th className="px-3 py-3 font-medium">Net revenue</th>
                        <th className="px-3 py-3 font-medium">Share link</th>
                        <th className="px-3 py-3 font-medium">Account</th>
                        <th className="px-3 py-3 font-medium">Status</th>
                        <th className="px-5 py-3 font-medium text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200">
                      {rows.map((r) => {
                        const rr = repReport(r);
                        return (
                          <tr key={r.id} className={r.active ? '' : 'bg-slate-50/40'}>
                            <td className="px-5 py-3">
                              <div className="text-slate-900 font-medium">{r.name}</div>
                              {r.email && <div className="text-[12px] text-slate-500">{r.email}</div>}
                            </td>
                            <td className="px-3 py-3">
                              <code className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-800 font-mono text-[12px]">{r.code}</code>
                            </td>
                            <td className="px-3 py-3 tabular-nums text-slate-700">
                              {r.default_discount_percent != null ? `+${r.default_discount_percent}%` : <span className="text-slate-400">—</span>}
                            </td>
                            <td className="px-3 py-3 tabular-nums text-slate-700">
                              {r.commission_percent != null ? (
                                `${r.commission_percent}%`
                              ) : report?.default_commission_percent != null ? (
                                <span title="Inherits the station default">{report.default_commission_percent}%</span>
                              ) : (
                                <span className="text-slate-400">—</span>
                              )}
                            </td>
                            <td className="px-3 py-3 tabular-nums text-slate-700">
                              {rr && rr.total_net_revenue_cents > 0 ? formatDollars(rr.total_net_revenue_cents) : <span className="text-slate-400">—</span>}
                            </td>
                            <td className="px-3 py-3">
                              <button
                                type="button"
                                onClick={() => handleCopy(r.code)}
                                className="inline-flex items-center gap-1.5 whitespace-nowrap text-[12px] text-slate-600 hover:text-slate-900"
                                title={shareUrl(r.code)}
                              >
                                {copiedCode === r.code ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
                                {copiedCode === r.code ? 'Copied' : 'Copy Link'}
                              </button>
                            </td>
                            <td className="px-3 py-3">
                              <LinkStatusBadge status={r.link_status} hasEmail={!!r.email} />
                              {/* CTV-673: resend (or first-send for pre-feature
                                  rows) without re-saving the record. */}
                              {!r.user_id && r.email && r.link_status !== 'email_in_use' && (
                                <button
                                  type="button"
                                  onClick={() => handleResendInvite(r.id)}
                                  disabled={resendingId === r.id}
                                  className="block mt-1.5 whitespace-nowrap text-[11px] text-blue-600 hover:underline disabled:opacity-50"
                                >
                                  {resentId === r.id
                                    ? 'Sent!'
                                    : r.link_status === 'invited'
                                      ? 'Resend Invite'
                                      : 'Send Invite'}
                                </button>
                              )}
                            </td>
                            <td className="px-3 py-3">
                              <span className={`text-[11px] font-medium px-2 py-0.5 rounded ${r.active ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-200 text-slate-700'}`}>
                                {r.active ? 'Active' : 'Disabled'}
                              </span>
                            </td>
                            <td className="px-5 py-3 text-right">
                              <div className="inline-flex gap-2 items-center justify-end">
                                <Link
                                  to={`/admin/${slug}/referrals/${r.id}`}
                                  className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[12px] font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 border border-blue-200 whitespace-nowrap"
                                >
                                  View Attributions <ChevronRight className="w-3.5 h-3.5" />
                                </Link>
                                <button
                                  type="button"
                                  onClick={() => { setEditing(r); setEditorOpen(true); }}
                                  className="p-1.5 text-slate-500 hover:text-slate-900 hover:bg-slate-100 rounded"
                                  title="Edit"
                                  aria-label="Edit"
                                >
                                  <Pencil className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}

        {editorOpen && (
          <SalespersonEditor
            initial={editing}
            defaultCommissionPercent={report?.default_commission_percent ?? null}
            onCancel={() => setEditorOpen(false)}
            onSave={async (payload) => {
              await upsert(payload);
              setEditorOpen(false);
            }}
        />
      )}
    </div>
  );
};

// CTV-673: whether this record has (or is getting) a station login. Linked
// reps see their own stats under "My referrals".
const LinkStatusBadge: React.FC<{
  status: SalespersonLinkStatus | undefined;
  hasEmail: boolean;
}> = ({ status, hasEmail }) => {
  if (status === 'linked') {
    return (
      <span className="inline-block whitespace-nowrap text-[11px] font-medium px-2 py-0.5 rounded bg-blue-100 text-blue-800">
        Linked
      </span>
    );
  }
  if (status === 'invited') {
    return (
      <span className="inline-block whitespace-nowrap text-[11px] font-medium px-2 py-0.5 rounded bg-amber-100 text-amber-800">
        Invite Sent
      </span>
    );
  }
  if (status === 'email_in_use') {
    return (
      <span
        className="inline-block whitespace-nowrap text-[11px] font-medium px-2 py-0.5 rounded bg-red-100 text-red-800"
        title="This email belongs to an account that can't be linked here."
      >
        Email in Use
      </span>
    );
  }
  return (
    <span
      className="inline-block whitespace-nowrap text-[11px] text-slate-400"
      title={hasEmail ? 'No account yet.' : 'Add an email to invite them to create an account.'}
    >
      No Account
    </span>
  );
};

// CTV-671: station-wide default commission %. Reps without a per-rep override
// inherit this; the % is frozen onto each campaign at submission time.
const DefaultCommissionCard: React.FC<{
  slug: string;
  value: number | null;
  onSaved: () => Promise<void>;
  onError: (msg: string) => void;
}> = ({ slug, value, onSaved, onError }) => {
  const [editing, setEditing] = React.useState(false);
  const [draft, setDraft] = React.useState(value != null ? String(value) : '');
  const [busy, setBusy] = React.useState(false);

  const save = async () => {
    let pct: number | null = null;
    if (draft.trim()) {
      const n = Number(draft);
      if (!Number.isFinite(n) || n < 0 || n > 100) {
        onError('Default commission must be 0–100, or blank to clear.');
        return;
      }
      pct = n;
    }
    setBusy(true);
    try {
      await apiClient.post('/salespeople/commission-default', {
        slug,
        defaultCommissionPercent: pct,
      });
      setEditing(false);
      await onSaved();
    } catch (e) {
      onError(e instanceof Error ? e.message : 'Failed to save default commission');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 mb-4 flex items-center justify-between gap-4 flex-wrap">
      <div>
        <div className="text-xs uppercase tracking-wide text-slate-500 mb-0.5">Default commission</div>
        <p className="text-sm text-slate-600">
          Applies to every rep without their own %. The % in effect when a campaign is
          submitted stays locked for all of that campaign&apos;s payments.
        </p>
      </div>
      {editing ? (
        <div className="flex items-center gap-2">
          <input
            type="number"
            min={0}
            max={100}
            step={0.5}
            autoFocus
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            className="w-24 p-2 border border-slate-300 rounded-md text-sm text-right focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
            aria-label="Default commission percent"
          />
          <span className="text-sm text-slate-600">%</span>
          <button
            type="button"
            onClick={save}
            disabled={busy}
            className="px-3 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md inline-flex items-center gap-1.5 disabled:opacity-60"
          >
            {busy && <Loader2 className="w-4 h-4 animate-spin" />}
            Save
          </button>
          <button
            type="button"
            onClick={() => { setEditing(false); setDraft(value != null ? String(value) : ''); }}
            disabled={busy}
            className="px-3 py-2 text-sm text-slate-700 hover:bg-slate-100 rounded-md"
          >
            Cancel
          </button>
        </div>
      ) : (
        <div className="flex items-center gap-3">
          <span className="text-2xl font-bold text-slate-900 tabular-nums">
            {value != null ? `${value}%` : <span className="text-slate-400 text-base font-medium">Not set</span>}
          </span>
          <button
            type="button"
            aria-label="Edit default commission"
            onClick={() => { setDraft(value != null ? String(value) : ''); setEditing(true); }}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 hover:bg-slate-50 rounded-md"
          >
            <Pencil className="w-3.5 h-3.5" />
            {value != null ? 'Edit' : 'Set Default'}
          </button>
        </div>
      )}
    </div>
  );
};

// CTV-671: net revenue each rep has brought in (payout-ledger figures, so net
// of CTVBuyer rev share and Stripe fees). Single series; commission and
// campaign count ride in the tooltip. Rendered only when there's revenue.
const RepRevenueChart: React.FC<{ report: CommissionReport }> = ({ report }) => {
  const data = report.reps
    .filter((r) => r.total_net_revenue_cents > 0)
    .sort((a, b) => b.total_net_revenue_cents - a.total_net_revenue_cents)
    .map((r) => ({
      name: r.name,
      netRevenueDollars: r.total_net_revenue_cents / 100,
      commissionDollars: r.total_commission_cents / 100,
      campaigns: r.campaign_count,
    }));
  if (data.length === 0) return null;

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 mb-4">
      <div className="text-xs uppercase tracking-wide text-slate-500 mb-3">Net revenue by salesperson</div>
      <div style={{ width: '100%', height: 240 }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
            <XAxis dataKey="name" tick={{ fontSize: 12, fill: '#64748b' }} stroke="#94a3b8" />
            <YAxis tickFormatter={formatDollarAxis} tick={{ fontSize: 12, fill: '#64748b' }} stroke="#94a3b8" />
            <Tooltip
              formatter={(value: number, name: string) => [formatDollars(value * 100), name]}
              labelFormatter={(label: string, payload) => {
                const row = payload?.[0]?.payload as { campaigns?: number } | undefined;
                return row?.campaigns != null ? `${label} · ${row.campaigns} campaign${row.campaigns === 1 ? '' : 's'}` : label;
              }}
            />
            <Bar dataKey="netRevenueDollars" name="Net revenue" fill="#3b82f6" radius={[3, 3, 0, 0]} maxBarSize={48} />
            <Bar dataKey="commissionDollars" name="Commission" fill="#10b981" radius={[3, 3, 0, 0]} maxBarSize={48} />
          </BarChart>
        </ResponsiveContainer>
      </div>
      <div className="mt-2 flex items-center gap-4 text-[12px] text-slate-600">
        <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-blue-500 inline-block" /> Net revenue</span>
        <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-emerald-500 inline-block" /> Commission</span>
      </div>
    </div>
  );
};

const SalespersonEditor: React.FC<{
  initial: SelfServiceSalesperson | null;
  defaultCommissionPercent: number | null;
  onCancel: () => void;
  onSave: (payload: {
    id?: string;
    name: string;
    email?: string | null;
    code: string;
    defaultDiscountPercent?: number | null;
    commissionPercent?: number | null;
    active?: boolean;
  }) => Promise<void>;
}> = ({ initial, defaultCommissionPercent, onCancel, onSave }) => {
  const [name, setName] = React.useState(initial?.name ?? '');
  const [email, setEmail] = React.useState(initial?.email ?? '');
  const [code, setCode] = React.useState(initial?.code ?? '');
  const [discount, setDiscount] = React.useState(
    initial?.default_discount_percent != null ? String(initial.default_discount_percent) : '',
  );
  const [commission, setCommission] = React.useState(
    initial?.commission_percent != null ? String(initial.commission_percent) : '',
  );
  // No local toggle state: the disable/re-enable action saves the flip
  // directly, so the stored value is the only truth the editor needs.
  const active = initial?.active ?? true;
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  // Auto-fill the code from the name only when creating. Editing keeps the
  // existing code unless the publisher explicitly changes it.
  React.useEffect(() => {
    if (initial) return;
    if (!name.trim()) { setCode(''); return; }
    setCode(suggestReferralCode(name));
  }, [name, initial]);

  // Shared by the submit button and the disable/re-enable action; the latter
  // passes an active override so it saves + closes in one click (CTV-673
  // review feedback) instead of arming local state for a second Save.
  const save = async (activeValue: boolean) => {
    if (!name.trim()) { setError('Name required'); return; }
    if (code.trim().length < 4 || code.trim().length > 16) { setError('Code must be 4–16 chars'); return; }
    let discountNum: number | null = null;
    if (discount.trim()) {
      const n = Number(discount);
      if (!Number.isFinite(n) || n <= 0 || n > 100) {
        setError('Discount must be > 0 and ≤ 100, or leave blank');
        return;
      }
      discountNum = n;
    }
    let commissionNum: number | null = null;
    if (commission.trim()) {
      const n = Number(commission);
      if (!Number.isFinite(n) || n < 0 || n > 100) {
        setError('Commission must be 0–100, or leave blank to use the station default');
        return;
      }
      commissionNum = n;
    }
    setError(null);
    setBusy(true);
    try {
      await onSave({
        id: initial?.id,
        name,
        email: email.trim() || null,
        code: code.trim().toUpperCase(),
        defaultDiscountPercent: discountNum,
        commissionPercent: commissionNum,
        active: activeValue,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setBusy(false);
    }
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    await save(active);
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/50 z-50" onClick={() => !busy && onCancel()} />
      {/* max-h + scroll: the commission field made the form taller than short
          viewports, leaving Create unreachable without it. */}
      <div className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-[90%] max-w-md max-h-[85vh] overflow-y-auto bg-white rounded-xl shadow-2xl border border-slate-200 p-5">
        <h3 className="text-base font-semibold text-slate-900 mb-1">
          {initial ? 'Edit Salesperson' : 'Add Salesperson'}
        </h3>
        <p className="text-sm text-slate-600 mb-4">
          Campaigns they refer are credited to their code. Add their email and they&apos;ll be
          invited to create a station login where they can track their own stats.
        </p>
        <form onSubmit={submit} className="space-y-3">
          <label className="block">
            <span className="text-xs font-medium uppercase tracking-wide text-slate-500">Name</span>
            <input
              type="text"
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Jane Doe"
              className="mt-1 w-full p-2.5 border border-slate-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
            />
          </label>
          <label className="block">
            <span className="text-xs font-medium uppercase tracking-wide text-slate-500">Email (optional)</span>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="jane@station.com"
              className="mt-1 w-full p-2.5 border border-slate-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
            />
            <p className="text-[11px] text-slate-500 mt-1">
              Sends a sign-up invite so they can log in and see their own referral stats.
              If they already have a station login under this email, it links automatically.
            </p>
          </label>
          <label className="block">
            <span className="text-xs font-medium uppercase tracking-wide text-slate-500">Code</span>
            <input
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 16))}
              placeholder="JANDOE"
              className="mt-1 w-full p-2.5 border border-slate-300 rounded-md text-sm font-mono tracking-widest focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
            />
            <p className="text-[11px] text-slate-500 mt-1">
              Uppercase letters and numbers only. Auto-filled from the name; override to set a vanity code.
            </p>
          </label>
          <label className="block">
            <span className="text-xs font-medium uppercase tracking-wide text-slate-500">Bonus plays % (optional)</span>
            <input
              type="number"
              min={1}
              max={100}
              step={1}
              value={discount}
              onChange={(e) => setDiscount(e.target.value)}
              placeholder="None"
              className="mt-1 w-full p-2.5 border border-slate-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
            />
            <p className="text-[11px] text-slate-500 mt-1">
              Lets the rep offer extra streaming ad plays as a closing tool, same price for the buyer. Leave blank for attribution only. When the buyer also has a promo code, the bigger of the two applies; prepay stacks on top of either.
            </p>
          </label>
          <label className="block">
            <span className="text-xs font-medium uppercase tracking-wide text-slate-500">Commission % (optional)</span>
            <input
              type="number"
              min={0}
              max={100}
              step={0.5}
              value={commission}
              onChange={(e) => setCommission(e.target.value)}
              placeholder={defaultCommissionPercent != null ? `Station default (${defaultCommissionPercent}%)` : 'Not set'}
              className="mt-1 w-full p-2.5 border border-slate-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
            />
            <p className="text-[11px] text-slate-500 mt-1">
              % of net revenue (after rev share and processing fees) credited to this rep. Leave blank to use the station default. The % in effect when a campaign is submitted stays locked for all of its payments.
            </p>
          </label>

          {error && <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2">{error}</p>}

          <div className="flex items-center justify-between gap-2 pt-2">
            {/* Disable / re-enable saves immediately and closes the modal
                (CTV-673 review feedback) — no second Save click. Any other
                edits in the form ride along, same validation as Save. */}
            {initial ? (
              <button
                type="button"
                onClick={() => save(!active)}
                disabled={busy}
                className={`text-sm font-medium ${
                  active ? 'text-red-600 hover:text-red-700' : 'text-emerald-700 hover:text-emerald-800'
                }`}
              >
                {active ? 'Disable Salesperson' : 'Re-enable Salesperson'}
              </button>
            ) : <span />}
            <div className="flex gap-2">
              <button
                type="button"
                onClick={onCancel}
                disabled={busy}
                className="px-4 py-2 text-sm text-slate-700 hover:bg-slate-100 rounded-md"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={busy}
                // CTV-673: stable hook for browser automation — headless
                // coordinate clicks miss this transform-centered modal.
                data-testid="salesperson-editor-submit"
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md inline-flex items-center gap-1.5 disabled:opacity-60"
              >
                {busy && <Loader2 className="w-4 h-4 animate-spin" />}
                {initial ? 'Save' : 'Create'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </>
  );
};

// CSV helpers — quote any field with commas, quotes, or newlines (RFC
// 4180-ish). Excel / Sheets open the resulting file natively.
function csvCell(v: string | number | null | undefined): string {
  const s = v == null ? '' : String(v);
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

// Format an ISO timestamp as `YYYY-MM-DD HH:MM` (UTC). Excel and Sheets
// parse this cleanly as a datetime; the raw `2026-04-30T05:18:08.322656+00`
// from Postgres reads as a string instead.
function formatCsvDate(iso: string | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return d.toISOString().slice(0, 16).replace('T', ' ');
}

function downloadCsv(filename: string, csv: string): void {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export default PublisherReferralsPage;
